<script>
import utility from '../../mixins/utility';

export default {
  name: 'NonDynamicTransportOverviewList',
  mixins: [
    utility,
  ],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      showCreateModal: false,
      productTypeId: null,
      filterBoundOutIn: 'All',
      boundOutInTypes: [
        'All', 'Outbound', 'Inbound', 'Out-/inbound',
      ],
      headers: [
        { text: 'Type', align: 'left', value: 'type' },
        { text: 'From', align: 'left', value: 'route[0].departure' },
        { text: 'Stop over', align: 'left', value: 'route[0].stopOver' },
        { text: 'To', align: 'left', value: 'route[0].arrival' },
        { text: 'Bound', align: 'left', value: 'bound' },
        { text: 'From date', align: 'left', value: 'fromDate' },
        { text: 'To date', align: 'left', value: 'toDate' },
        { text: 'Days', align: 'left', sortable: false, width: '200px' },
        { text: 'Airline', align: 'left', value: 'route[0].transportCarrier' },
        { text: 'FNo.', align: 'left', value: 'ref' },
        { text: 'PNR', align: 'left', value: 'PNR' },
        { text: 'Type', align: 'left', value: 'allotment' },
      ],
      pagination: {
        rowsPerPage: 10,
        descending: false,
        sortBy: 'name',
      },
      transportTypes: [],
      selectedTransportType: null,
    };
  },
  computed: {
    filteredItems() {
      const outIn = this.filterBoundOutIn.toLowerCase();
      const searchStr = (this.search || '').toLowerCase();
      const emptyRoute = {
        departure: '',
        arrival: '',
        ref: '',
      };
      return this.items.filter(
        transport =>
          (
            outIn === 'all' || transport.bound === outIn
          ) && (
            searchStr === '' ||
            [
              transport.route[0].departure,
              (transport.route[1] || emptyRoute).departure,
              transport.route[0].arrival,
              (transport.route[1] || emptyRoute).arrival,
              transport.route[0].ref,
              (transport.route[1] || emptyRoute).ref,
              transport.PNR,
            ].filter(value => value || '').join('\t').toLowerCase().indexOf(searchStr) !== -1
          ),
      );
    },
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.getTransportTypes();
      },
    },
  },
  methods: {
    gotoTwenty() {
      location.href = `${process.env.TWENTY_FRONT_PAGE}/production/products/external/farewise`;
    },
    getTransportTypes() {
      const uniqueFlights = {};
      this.items.forEach((value) => {
        uniqueFlights[value.type] = {
          value: value.type,
          text: value.type.charAt(0).toUpperCase() + value.type.slice(1),
        };
      });
      this.selectedTransportType = (this.transportTypes = Object.values(uniqueFlights))[0].value;
    },
    filtering(item) {
      return this.product(item.productTypeId) && this.indexOf(item.name);
    },
    indexOf(value) {
      return !this.search || value.toLowerCase().indexOf(this.search.toLowerCase()) !== -1;
    },
    product(value) {
      return !this.productTypeId || value === this.productTypeId;
    },
    closeModalAndClear() {
      this.showCreateModal = false;
      this.search = '';
      this.productTypeId = null;
    },
    formatDate(date) {
      return date ? this.$moment(date).format('DD-MM-YYYY') : '';
    },
  },
};
</script>

<template>
  <v-container
    class="ma-0 fluid">
    <v-layout row>
      <v-flex md1>
        <v-select
          clearable
          clear-icon="clear"
          item-text="text"
          item-value="value"
          label="Transport type"
          v-model="selectedTransportType"
          :loading="loading"
          :items="transportTypes"
        />
      </v-flex>
      <v-flex md4>
        <v-text-field
          v-model="search"
          :loading="loading"
          :label="$t('inputs.placeholders.search')"
          append-icon="search"
          class="px-4"
          clear-icon="clear"
          clearable
        />
      </v-flex>
      <v-flex md1>
        <v-select
          v-model="filterBoundOutIn"
          :items="boundOutInTypes"
          :item-value="(item) => {return item.toLowerCase();}"
          label="Bound"
        />
      </v-flex>
      <v-flex shrink>
        <v-btn
          icon
          :loading="loading"
          @click="$emit('refresh')">
          <v-icon>cached</v-icon>
        </v-btn>
      </v-flex>
      <v-flex md5>
        <v-btn
          class="right"
          large
          color="primary"
          :loading="loading"
          :disabled="loading"
          @click="$emit('create')">
          {{ $t('inputs.actions.add') }}
        </v-btn>
        <v-btn
          class="right"
          large
          color="primary"
          @click="gotoTwenty();">
          FareWise
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout row>
      <v-flex md14>
        <v-data-table
          class="elevation-1 small-padding"
          item-key="id"
          :headers="headers"
          :items="filteredItems"
          :pagination.sync="pagination"
          :loading="loading"
        >
          <template v-slot:items="props">
            <tr
              class="table-row"
              @click="$router.push({
                name: 'NonDynamicTransportCreate',
                params: { id: props.item.id }
              })">
              <td>
                {{ props.item.type | capitalizeFirstLetter }}</td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">{{ props.item.route[0].departure }}</div>
                  </template>
                  <span>{{ props.item.route[0].departureFullname }}</span>
                </v-tooltip>
                <v-tooltip top v-if="props.item.route[1]">
                  <template v-slot:activator="{ on }">
                    <div v-on="on">{{ props.item.route[1].departure }}</div>
                  </template>
                  <span>{{ props.item.route[1].departureFullname }}</span>
                </v-tooltip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">{{ props.item.route[0].stopOver }}</div>
                  </template>
                  <span>{{ props.item.route[0].stopOverFullname }}</span>
                </v-tooltip>
                <v-tooltip top v-if="props.item.route[1]">
                  <template v-slot:activator="{ on }">
                    <div v-on="on">{{ props.item.route[1].stopOver }}</div>
                  </template>
                  <span>{{ props.item.route[1].stopOverFullname }}</span>
                </v-tooltip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">{{ props.item.route[0].arrival }}</div>
                  </template>
                  <span>{{ props.item.route[0].arrivalFullname }}</span>
                </v-tooltip>
                <v-tooltip top v-if="props.item.route[1]">
                  <template v-slot:activator="{ on }">
                    <div v-on="on">{{ props.item.route[1].arrival }}</div>
                  </template>
                  <span>{{ props.item.route[1].arrivalFullname }}</span>
                </v-tooltip>
              </td>
              <td>{{ props.item.bound | capitalizeFirstLetter }}</td>
              <td>{{ formatDate(props.item.fromDate) }}</td>
              <td>{{ formatDate(props.item.toDate) }}</td>
              <td>
                <div v-if="props.item.route[0].days.monday" class="day-valid">M</div>
                <div v-else class="day-invalid">M</div>
                <div v-if="props.item.route[0].days.tuesday" class="day-valid">T</div>
                <div v-else class="day-invalid">T</div>
                <div v-if="props.item.route[0].days.wednesday" class="day-valid">W</div>
                <div v-else class="day-invalid">W</div>
                <div v-if="props.item.route[0].days.thursday" class="day-valid">T</div>
                <div v-else class="day-invalid">T</div>
                <div v-if="props.item.route[0].days.friday" class="day-valid">F</div>
                <div v-else class="day-invalid">F</div>
                <div v-if="props.item.route[0].days.saturday" class="day-valid">S</div>
                <div v-else class="day-invalid">S</div>
                <div v-if="props.item.route[0].days.sunday" class="day-valid">S</div>
                <div v-else class="day-invalid">S</div>
                <br v-if="props.item.route[1]" style="clear:both" />
                <div v-if="props.item.route[1]">
                  <div v-if="props.item.route[1].days.monday" class="day-valid">M</div>
                  <div v-else class="day-invalid">M</div>
                  <div v-if="props.item.route[1].days.tuesday" class="day-valid">T</div>
                  <div v-else class="day-invalid">T</div>
                  <div v-if="props.item.route[1].days.wednesday" class="day-valid">W</div>
                  <div v-else class="day-invalid">W</div>
                  <div v-if="props.item.route[1].days.thursday" class="day-valid">T</div>
                  <div v-else class="day-invalid">T</div>
                  <div v-if="props.item.route[1].days.friday" class="day-valid">F</div>
                  <div v-else class="day-invalid">F</div>
                  <div v-if="props.item.route[1].days.saturday" class="day-valid">S</div>
                  <div v-else class="day-invalid">S</div>
                  <div v-if="props.item.route[1].days.sunday" class="day-valid">S</div>
                  <div v-else class="day-invalid">S</div>
                </div>
              </td>
              <td>{{ props.item.route[0].transportCarrier }}</td>
              <td>
                {{ props.item.route[0].ref }}
                <br v-if="props.item.route[1]" style="clear:both" />
                <div v-if="props.item.route[1]">{{ props.item.route[1].ref }}</div>
              </td>
              <td>{{ props.item.PNR }}</td>
              <td>{{ props.item.allotment }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style>
  .table-row {
    cursor: pointer;
  }

  .small-padding td {
    padding: 12px 6px 12px 6px !important;
  }

  .small-padding th {
    padding: 12px 6px 12px 6px !important;
  }

  .day-valid {
    text-align: center;
    margin: 2px;
    padding: 2px 5px 8px 5px;
    border: 1px solid black;
    border-radius: 5px 5px;
    background-color: green;
    width: 20px;
    height: 25px;
    float: left;
    display: inline-block;
  }

  .day-invalid {
    text-align: center;
    margin: 2px;
    padding: 2px 5px 8px 5px;
    border: 1px solid black;
    border-radius: 5px 5px;
    background-color: red;
    width: 20px;
    height: 25px;
    float: left;
    display: inline-block;
  }
</style>



// WEBPACK FOOTER //
// src/components/non-dynamic-transport/NonDynamicTransportOverviewList.vue